import { useState } from "react";
import { DraftPicksSheet } from "../components/DraftPicksSheet.tsx";
import useTitleBar from "../hooks/useTitleBar.tsx";
import {
  MobileContentHero,
  MobileStatRow,
  mobileActionButtonClassNames,
} from "../mobile/mobilePrimitives.tsx";
import { helpers } from "../util/helpers.ts";
import i18n from "../util/i18n.ts";
import { useLocalPartial } from "../util/local.ts";
import { createMobileRouteSheetHref } from "../util/mobileCrossTabLink.ts";
import { getMarketNavigationGroups, type MobileNavigationEntry } from "../util/phaseNavigation.ts";
import type { View } from "../../../../common/types.ts";
import {
  getMarketHubPrimaryTask,
  getMarketHubResourceSummary,
  type MarketHubResource,
} from "./marketHubTask.ts";

const statRowTone = (status: MarketHubResource["status"]) => {
  if (status === "danger" || status === "warning") {
    return "warning" as const;
  }
  if (status === "ok") {
    return "positive" as const;
  }
  return "neutral" as const;
};

// B2b content-first migration (v5Content contract): resources render as plain
// stat rows — label + value only, no detail sentences, no status badges.
const MarketHubResourceRows = ({
  resources,
  onOpenDraftPicks,
}: {
  resources: MarketHubResource[];
  onOpenDraftPicks: () => void;
}) => (
  <div className="d-grid gap-2 mb-3" data-v5-market-resource-stat-rows="">
    {resources.map((resource) => {
      const value = resource.valueKey
        ? i18n.t(resource.valueKey, {
            ns: "ui",
            defaultValue: resource.value,
            ...resource.valueParams,
          })
        : resource.value;
      const row = (
        <MobileStatRow
          highlighted={resource.status === "danger"}
          label={i18n.t(resource.labelKey, {
            ns: "ui",
            defaultValue: resource.defaultLabel,
          })}
          tone={statRowTone(resource.status)}
          value={value}
        />
      );

      if (resource.sheet === "draftPicks") {
        return (
          <button
            className="btn btn-link text-body text-decoration-none text-start p-0 w-100"
            data-market-hub-resource-sheet="draftPicks"
            data-v5-market-resource-key={resource.key}
            data-v5-market-resource-status={resource.status}
            key={resource.key}
            onClick={onOpenDraftPicks}
            type="button"
          >
            {row}
          </button>
        );
      }

      return resource.path ? (
        <a
          className="text-body text-decoration-none"
          data-v5-market-resource-key={resource.key}
          data-v5-market-resource-status={resource.status}
          href={createMobileRouteSheetHref(helpers.leagueUrl(resource.path), {
            pageID: "marketHub",
          })}
          key={resource.key}
        >
          {row}
        </a>
      ) : (
        <div
          data-v5-market-resource-key={resource.key}
          data-v5-market-resource-status={resource.status}
          key={resource.key}
        >
          {row}
        </div>
      );
    })}
  </div>
);

const MarketHubTargetRow = ({
  entry,
  onOpenDraftPicks,
}: {
  entry: MobileNavigationEntry;
  onOpenDraftPicks: () => void;
}) => {
  const label = i18n.t(entry.labelKey, {
    ns: "ui",
    defaultValue: entry.defaultLabel,
  });
  const row = (
    <>
      <span className="fw-semibold">{label}</span>
      <span aria-hidden="true" className="text-body-secondary">
        ›
      </span>
    </>
  );

  if (entry.sheet === "draftPicks") {
    return (
      <button
        className="list-group-item list-group-item-action d-flex justify-content-between align-items-center gap-2 text-start"
        data-market-hub-other-action={entry.key}
        onClick={onOpenDraftPicks}
        type="button"
      >
        {row}
      </button>
    );
  }

  if (!entry.available || entry.path === undefined) {
    return (
      <div
        aria-disabled="true"
        className="list-group-item d-flex justify-content-between align-items-center gap-2 text-body-secondary"
        data-market-hub-other-action={entry.key}
      >
        <span className="fw-semibold">{label}</span>
        <span className="badge text-bg-light border fw-normal">
          {i18n.t("mobileNav.group.unavailable", {
            ns: "ui",
            defaultValue: "Temporarily unavailable",
          })}
        </span>
      </div>
    );
  }

  return (
    <a
      className="list-group-item list-group-item-action d-flex justify-content-between align-items-center gap-2 text-body text-decoration-none"
      data-market-hub-other-action={entry.key}
      href={createMobileRouteSheetHref(helpers.leagueUrl(entry.path), {
        pageID: "marketHub",
      })}
    >
      {row}
    </a>
  );
};

const mergeEntriesByKey = (...entryGroups: MobileNavigationEntry[][]) => {
  const seen = new Set<string>();

  return entryGroups.flat().filter((entry) => {
    if (seen.has(entry.key)) {
      return false;
    }
    seen.add(entry.key);
    return true;
  });
};

const MarketHub = ({ marketState, phase: viewPhase, teamMarketContext }: View<"marketHub">) => {
  const { phase: localPhase, userTid } = useLocalPartial(["phase", "userTid"]);
  const phase = viewPhase ?? localPhase;
  // M3-4: the draft-picks page was retired into the DraftPicksSheet overlay; the
  // market hub hosts it, so the "My Picks" entry / assets CTA / trade-closed
  // resource open it in place instead of navigating to /draft_picks.
  const [draftPicksSheetOpen, setDraftPicksSheetOpen] = useState(false);
  const openDraftPicksSheet = () => setDraftPicksSheetOpen(true);
  const title = i18n.t("marketHub.title", {
    ns: "ui",
    defaultValue: "Market",
  });
  const groups = getMarketNavigationGroups({
    hasOpenFreeAgency:
      marketState?.canSignFreeAgents && (teamMarketContext?.minRosterDeficit ?? 0) > 0,
    hasPendingNegotiations: marketState?.hasPendingNegotiations,
    needsExpansionProtection: marketState?.needsExpansionProtection,
    phase,
  });
  const recommendedEntry = groups.find((group) => group.key === "recommended")?.entries[0];
  const availableEntries = groups.find((group) => group.key === "available")?.entries ?? [];
  const unavailableEntries = groups.find((group) => group.key === "unavailable")?.entries ?? [];
  const primaryTask = getMarketHubPrimaryTask({
    marketState,
    phase,
    recommendedEntry,
    teamMarketContext,
  });
  const primaryActionState = primaryTask
    ? primaryTask.primaryAction.sheet
      ? `sheet:${primaryTask.primaryAction.sheet}`
      : `path:${primaryTask.primaryAction.path?.join("/") ?? ""}`
    : undefined;
  const resources = getMarketHubResourceSummary({
    marketState,
    teamMarketContext,
  });
  // 法则二: the primary CTA already carries the recommended route, so the
  // target rows only list routes that differ from it.
  const primaryOpensDraftPicksSheet = primaryTask?.primaryAction.sheet === "draftPicks";
  const primaryPath = primaryTask?.primaryAction.path?.join("/");
  const targetEntries = [...(recommendedEntry ? [recommendedEntry] : []), ...availableEntries]
    .filter((entry) =>
      // The primary CTA already carries the recommended action; drop the target
      // row that duplicates it, whether it navigates (path) or opens the sheet.
      entry.sheet === "draftPicks"
        ? !primaryOpensDraftPicksSheet
        : entry.path?.join("/") !== primaryPath,
    )
    .slice(0, 3);
  // Split the available routes only to preserve their prior ordering, then
  // merge them into one deduplicated, secondary-only list below the primary CTA.
  const targetKeys = new Set(targetEntries.map((entry) => entry.key));
  const secondaryEntries = availableEntries.filter((entry) => !targetKeys.has(entry.key));
  const moreActionEntries = mergeEntriesByKey(targetEntries, secondaryEntries);

  useTitleBar({
    title,
  });

  return (
    <div
      className="mx-auto pb-3"
      data-desktop-production-view="marketHub"
      data-v5-market-content-first-screen=""
      style={{ maxWidth: 760 }}
    >
      {/* U1: one current task and its reason lead the three resources and the
          single primary CTA. All other market routes stay below the fold. */}
      <MobileContentHero className="mb-3">
        {primaryTask ? (
          <div
            className="market-hub-entry-recommended mb-3"
            data-v5-market-primary-action={primaryActionState}
            data-v5-market-primary-phase={phase}
            data-v5-market-primary-task={primaryTask.key}
          >
            <h2 className="h5 mb-1">
              {i18n.t(primaryTask.titleKey, {
                ns: "ui",
                defaultValue: primaryTask.defaultTitle,
              })}
            </h2>
            <p className="text-body-secondary mb-0">
              {i18n.t(primaryTask.descriptionKey, {
                ns: "ui",
                defaultValue: primaryTask.defaultDescription,
                ...primaryTask.descriptionParams,
              })}
            </p>
          </div>
        ) : null}
        <MarketHubResourceRows onOpenDraftPicks={openDraftPicksSheet} resources={resources} />
        {primaryTask ? (
          primaryOpensDraftPicksSheet ? (
            <button
              className={`${mobileActionButtonClassNames.primary} w-100`}
              data-v5-market-primary-action-control={primaryActionState}
              data-v5-market-single-cta=""
              onClick={openDraftPicksSheet}
              type="button"
            >
              {i18n.t(primaryTask.primaryAction.labelKey, {
                ns: "ui",
                defaultValue: primaryTask.primaryAction.defaultLabel,
              })}
            </button>
          ) : (
            <a
              className={`${mobileActionButtonClassNames.primary} w-100`}
              data-v5-market-primary-action-control={primaryActionState}
              data-v5-market-single-cta=""
              href={createMobileRouteSheetHref(helpers.leagueUrl(primaryTask.primaryAction.path!), {
                pageID: "marketHub",
              })}
            >
              {i18n.t(primaryTask.primaryAction.labelKey, {
                ns: "ui",
                defaultValue: primaryTask.primaryAction.defaultLabel,
              })}
            </a>
          )
        ) : null}
      </MobileContentHero>
      <nav
        aria-label={i18n.t("marketHub.modulesTitle", {
          ns: "ui",
          defaultValue: "Market modules",
        })}
        className="mb-4"
        data-market-hub-modules=""
      >
        <h2 className="h6 mb-2">
          {i18n.t("marketHub.modulesTitle", {
            ns: "ui",
            defaultValue: "Market modules",
          })}
        </h2>
        <div className="list-group list-group-flush border rounded-3 overflow-hidden">
          {moreActionEntries.map((entry) => (
            <MarketHubTargetRow
              entry={entry}
              key={entry.key}
              onOpenDraftPicks={openDraftPicksSheet}
            />
          ))}
          {unavailableEntries.length > 0 ? (
            <>
              {unavailableEntries.map((entry) => (
                <MarketHubTargetRow
                  entry={entry}
                  key={entry.key}
                  onOpenDraftPicks={openDraftPicksSheet}
                />
              ))}
            </>
          ) : null}
        </div>
      </nav>
      <DraftPicksSheet
        onClose={() => setDraftPicksSheetOpen(false)}
        open={draftPicksSheetOpen}
        tid={userTid}
      />
    </div>
  );
};

export default MarketHub;
